import { PayeeAccountTypesEnum } from "@propertelligent/client-api";

export interface LookupOption {
  Id: number | string;
  Name: string;
}

/**
 * Payee Type dropdown options for the Payable "Add Payment" form's cascading
 * Payee Type -> Select Payee -> Payment Types flow.
 *
 * Built from the existing manual-create payee-types lookup
 * (PaymentService.getApiPayeeAccountPayeeTypes) plus two additional system types that are
 * hidden from that manual-create dropdown (PayeeAccountTypesEnum's [HideInDropDown]) but are
 * valid *payable* payee types once an Organization User or Contractor has a payee account --
 * their own name is the payee. Ids/labels must match PayeeAccountTypesEnum.OrganizationUser /
 * .Contractor and their backend [Description] strings ("Organization User" / "Contractor").
 */
export const buildPayablePayeeTypeOptions = (
  accountPayeeTypes: LookupOption[]
): LookupOption[] => [
  ...accountPayeeTypes,
  { Id: PayeeAccountTypesEnum.OrganizationUser, Name: "Organization User" },
  { Id: PayeeAccountTypesEnum.Contractor, Name: "Contractor" },
];

/**
 * Builds the Dynamic-LINQ FullyQualifiedFilters string used to fetch the "Select Payee"
 * dropdown options (PaymentService.getApiPayeeAccountOrganizationlist) for a given selected
 * Payee Type on the Payable Add Payment form.
 *
 * - No type selected: the original default -- every organization-flagged payee account
 *   (IsOrganizationAccount == true).
 * - Organization User / Contractor: every payee account of that type, regardless of
 *   IsOrganizationAccount -- an org user's/contractor's *default* payee account (created
 *   automatically when they're created) is not IsOrganizationAccount-flagged; only a
 *   Plaid-connected one is (see PaymentMethodService.AddReferencedPayeeAccount). Restricting
 *   by that flag would hide every org user/contractor who hasn't connected Plaid.
 * - Any other explicit type (Utility Company, Vendor, etc.): IsOrganizationAccount == true
 *   AND PayeeAccountTypeId == the selected type -- same base restriction as before, narrowed
 *   to the selected type.
 */
export const buildPayeeAccountsFilterForType = (
  payeeTypeId?: string | number | null
): string => {
  const typeId = Number(payeeTypeId);
  if (!typeId) {
    return "IsOrganizationAccount==true";
  }
  if (
    typeId === PayeeAccountTypesEnum.OrganizationUser ||
    typeId === PayeeAccountTypesEnum.Contractor
  ) {
    return `PayeeAccountTypeId==${typeId}`;
  }
  return `IsOrganizationAccount==true&&PayeeAccountTypeId==${typeId}`;
};
